import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { formatTime, calculateDrift } from "../utils/videoSyncManager";

const loadDailymotionAPI = () => {
  return new Promise((resolve) => {
    if (window.DM) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://api.dmcdn.net/all.js";
    tag.onload = () => resolve();
    document.getElementsByTagName("head")[0].appendChild(tag);
  });
};

function DailymotionPlayer({ videoId, isHost, socketManager, partyStatus }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [drift, setDrift] = useState(0);
  const heartbeatIntervalRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);

  useEffect(() => {
    let player = null;
    const initPlayer = async () => {
      await loadDailymotionAPI();
      
      player = window.DM.player(containerRef.current, {
        video: videoId,
        width: "100%",
        height: "100%",
        params: {
          controls: false,
          autoplay: false,
        },
      });

      playerRef.current = player;

      player.addEventListener("apiready", () => {
        setIsReady(true);
      });

      player.addEventListener("durationchange", () => {
        const dur = player.duration;
        setDuration(dur);
      });

      player.addEventListener("play", () => {
        setIsPlaying(true);
        if (isHost) {
          console.log("[WP-DEBUG] Dailymotion host play → emitPlay", {
            currentTime: player.currentTime,
          });
          socketManager.emitPlay(player.currentTime);
        }
      });

      player.addEventListener("pause", () => {
        setIsPlaying(false);
        if (isHost) {
          socketManager.emitPause(player.currentTime);
        }
      });

      player.addEventListener("seeking", () => {
        if (isHost) {
          socketManager.emitSeek(player.currentTime);
        }
      });
    };

    initPlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current = null;
      }
    };
  }, [videoId, isHost, socketManager]);

  useEffect(() => {
    if (!isReady) return;
    timeUpdateIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        setCurrentTime(playerRef.current.currentTime || 0);
      }
    }, 100);
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [isReady]);

  useEffect(() => {
    if (!isHost || !isReady) return;
    heartbeatIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        socketManager.emitHeartbeat(playerRef.current.currentTime, !playerRef.current.paused);
      }
    }, 5000);
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isHost, isReady, socketManager]);

  useEffect(() => {
    if (isHost || !socketManager || !isReady) {
      if (!isHost && socketManager && !isReady) {
        console.log(
          "[WP-DEBUG] Dailymotion participant sync listeners NOT attached yet (isReady=false)"
        );
      }
      return;
    }

    console.log("[WP-DEBUG] Dailymotion participant sync listeners attached", {
      isReady,
    });

    const handleSyncState = (data) => {
      console.log("[WP-DEBUG] Dailymotion recv sync-state", {
        isReady,
        hasPlayer: !!playerRef.current,
        data,
      });
      if (!playerRef.current) return;
      const { currentTime: targetTime, isPlaying: targetPlaying } = data;
      const currentVideoTime = playerRef.current.currentTime;
      const currentDrift = calculateDrift(currentVideoTime, targetTime);
      setDrift(currentDrift);

      if (targetPlaying) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }

      if (currentDrift > 2) {
        setIsSyncing(true);
        playerRef.current.seek(targetTime);
        setTimeout(() => setIsSyncing(false), 1000);
      }
    };

    const handlePlay = (data) => {
      console.log("[WP-DEBUG] Dailymotion recv play", {
        isReady,
        hasPlayer: !!playerRef.current,
        data,
      });
      if (!playerRef.current) return;
      setIsSyncing(true);
      playerRef.current.seek(data.currentTime);
      playerRef.current.play();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handlePause = (data) => {
      if (!playerRef.current) return;
      setIsSyncing(true);
      playerRef.current.seek(data.currentTime);
      playerRef.current.pause();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handleSeek = (data) => {
      if (!playerRef.current) return;
      setIsSyncing(true);
      playerRef.current.seek(data.currentTime);
      setTimeout(() => setIsSyncing(false), 500);
    };

    socketManager.on("sync-state", handleSyncState);
    socketManager.on("play", handlePlay);
    socketManager.on("pause", handlePause);
    socketManager.on("seek", handleSeek);

    setTimeout(() => socketManager.requestSync(), 500);

    return () => {
      socketManager.off("sync-state", handleSyncState);
      socketManager.off("play", handlePlay);
      socketManager.off("pause", handlePause);
      socketManager.off("seek", handleSeek);
    };
  }, [isHost, socketManager, isReady]);

  const togglePlayPause = () => {
    if (!playerRef.current) return;
    if (playerRef.current.paused) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  };

  const handleSeek = (e) => {
    if (!playerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    playerRef.current.seek(seekTime);
    if (isHost) {
      socketManager.emitSeek(seekTime);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative bg-slate-950 rounded-xl overflow-hidden border border-slate-700">
      <div ref={containerRef} className="relative w-full aspect-video bg-black" />
      
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-300 text-sm">Loading Dailymotion player...</p>
          </div>
        </div>
      )}

      {isSyncing && (
        <div className="absolute top-4 left-4 bg-emerald-500/90 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 z-10">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Syncing...
        </div>
      )}

      {isHost && (
        <div className="absolute top-4 right-4 bg-violet-500/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold z-10">
          HOST
        </div>
      )}

      {!isHost && (
        <div className="absolute top-4 right-4 bg-slate-800/90 text-slate-300 px-3 py-1.5 rounded-full text-xs z-10">
          Following host
        </div>
      )}

      {isReady && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 z-10">
          <div
            className="w-full h-1.5 bg-slate-700 rounded-full cursor-pointer mb-3 group"
            onClick={isHost ? handleSeek : undefined}
          >
            <div
              className="h-full bg-blue-500 rounded-full relative group-hover:bg-blue-400 transition"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition" />
            </div>
          </div>

          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <button
                onClick={isHost ? togglePlayPause : undefined}
                disabled={!isHost}
                className={`p-2 rounded-full transition ${
                  isHost ? "hover:bg-white/20 active:scale-95" : "opacity-50 cursor-not-allowed"
                }`}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>
              <span className="text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              {!isHost && drift > 0.5 && (
                <span className="text-xs text-emerald-400">
                  Drift: {drift.toFixed(1)}s
                </span>
              )}
            </div>
          </div>

          {partyStatus === "waiting" && (
            <div className="mt-2 text-center text-sm text-slate-400">
              Waiting for party to start...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DailymotionPlayer;
